import { renderToString } from 'react-dom/server';
import Swal from 'sweetalert2';

import './notification-manager.css';

export enum NotificationSeverity {
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  INFO = 'INFO',
}

type ToastInput = {
  message: string;
  severity: NotificationSeverity;
};

type AlertInput = {
  message: string;
  severity: NotificationSeverity;
  showCloseBtn?: boolean;
  showOkBtn?: boolean;
  showConfirmBtn?: boolean;
};

const generateSwalOptions = (
  message: string,
  severity: NotificationSeverity,
  isToast: boolean = false,
  showCloseBtn?: boolean,
  showOkBtn?: boolean,
  showConfirmBtn?: boolean,
) => {
  const defaultOptions = {
    text: message,
    timer: 3000,
    showCloseButton: !isToast && showCloseBtn,
    showOkButton: (!isToast && severity === NotificationSeverity.ERROR) || (!isToast && showOkBtn),
    showConfirmButton:
      (!isToast && severity === NotificationSeverity.ERROR) || (!isToast && showConfirmBtn),
  };

  const baseSwalOptions = {
    ...defaultOptions,
    html: renderToString(
      <div className="toast-content">
        <p>{message}</p>
      </div>,
    ),
    iconColor: severity !== NotificationSeverity.ERROR ? 'default' : 'white',
    backdrop: !isToast,
    position: isToast ? 'top-right' : 'center',
    timerProgressBar: !!isToast,
    footer: false,
  };

  switch (severity) {
    case NotificationSeverity.SUCCESS:
      return {
        ...baseSwalOptions,
        icon: 'success',
        customClass: {
          popup: 'success-container swal2-toast-popup',
          icon: 'swal2-toast-icon',
        },
      };
    case NotificationSeverity.WARNING:
      return {
        ...baseSwalOptions,
        icon: 'warning',
        customClass: {
          popup: 'warning-container swal2-toast-popup',
          icon: 'swal2-toast-icon',
        },
      };
    case NotificationSeverity.INFO:
      return {
        ...baseSwalOptions,
        icon: 'info',
        customClass: {
          popup: 'info-container swal2-toast-popup',
          icon: 'swal2-toast-icon',
        },
      };
    case NotificationSeverity.ERROR:
      return {
        ...baseSwalOptions,
        icon: 'error',
        customClass: {
          popup: 'error-container swal2-toast-popup',
          icon: 'swal2-toast-icon',
          ...(!isToast && { confirmButton: 'error-btn' }),
        },
        timer: isToast ? 5000 : 100000,
      };
    default:
      console.warn('Unsupported severity level:', severity);
      return {};
  }
};

export const showToast = ({ message, severity }: ToastInput) => {
  const options = generateSwalOptions(message, severity, true);
  Swal.fire(options);
};

export const showAlert = ({
  message,
  severity,
  showCloseBtn = false,
  showOkBtn = false,
  showConfirmBtn = false,
}: AlertInput) => {
  if (message.includes('Not authorized')) {
    showToast({ message, severity });
    return;
  }

  const options = generateSwalOptions(
    message,
    severity,
    false,
    showCloseBtn,
    showOkBtn,
    showConfirmBtn,
  );
  Swal.fire(options);
};
